import { defaultApi, defaultClient } from '@mint-sdk/api-core';
import { z } from 'zod';

import {
  granularityLevelSchema,
  ingestionContextStrictSchema,
  paginationSchema,
} from '../shared/schemes.js';

const CommonTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  email: z.string().optional(),
  sheetName: z.string().nullable(),
  created: z.string(),
  granularityLevel: granularityLevelSchema,
  platform: z.string().optional().nullable(),
  dateOrder: z.string(),
  numberFormat: z.string(),
  footerRowsCount: z.number(),
  collaborators: z.array(
    z.object({
      userId: z.number(),
      email: z.string(),
      collaborationLevel: z.enum(['owner', 'collaborator']),
    }),
  ),
});

const TemplateListItemV1Schema = CommonTemplateSchema.extend({
  campaignId: z.number(),
  campaign: z.string(),
});

const TemplateListItemV2Schema = CommonTemplateSchema.extend({
  mediaPlanId: z.coerce.string(), // TODO: replace with strict type after BE release
  mediaPlanName: z.string(),
  activityId: z.coerce.string(), // TODO: replace with strict type after BE release
  activityName: z.string(),
});

export type TemplateListItem = TemplateListItemV1 | TemplateListItemV2;
export type TemplateListItemV1 = z.infer<typeof TemplateListItemV1Schema>;
export type TemplateListItemV2 = z.infer<typeof TemplateListItemV2Schema>;

const TemplatesListQuerySchema = z.object({
  search: z.string().prefault(''),
  pageNumber: z.number().prefault(1),
  itemsPerPage: z.number().prefault(50),
  template: z.array(z.string()).optional(),
  email: z.array(z.string()).optional(),
  campaign: z.array(z.string()).optional(),
  activity: z.array(z.string()).optional(),
  platform: z.array(z.string()).optional(),
  owner: z.array(z.string()).optional(),
  granularityLevel: z.array(z.string()).optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});

const TemplatesListV1ResponseSchema = z.object({
  data: z
    .object({
      items: z.array(TemplateListItemV1Schema),
      additional: z.object({}).nullable().prefault({}),
    })
    .extend(paginationSchema.shape),
});

const TemplatesListV2ResponseSchema = z.object({
  data: z
    .object({
      items: z.array(TemplateListItemV2Schema),
      additional: z.object({}).nullable().prefault({}),
    })
    .extend(paginationSchema.shape),
});

export type TemplatesListV1Response = z.infer<
  typeof TemplatesListV1ResponseSchema
>;
export type TemplatesListV1QueryResult = TemplatesListV1Response['data'];

export type TemplatesListV2Response = z.infer<
  typeof TemplatesListV2ResponseSchema
>;
export type TemplatesListV2QueryResult = TemplatesListV2Response['data'];

export type TemplatesListQueryResult =
  | TemplatesListV1QueryResult
  | TemplatesListV2QueryResult;

export type TemplatesListResponse =
  | TemplatesListV1Response
  | TemplatesListV2Response;

export const getTemplatesListV1QueryOptions = defaultClient.query({
  url: 'data-ingestion/templates/' as const,
  method: 'GET',
  querySchema: TemplatesListQuerySchema,
  responseSchema: TemplatesListV1ResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data): TemplatesListV1QueryResult {
    // Sort collaborators to put owners first
    for (const template of data.data.items) {
      template.collaborators.sort((collaborator) =>
        collaborator.collaborationLevel === 'owner' ? -1 : 1,
      );
    }
    return data.data;
  },
  keyPrefix: ['templatesList'],
});

export const getTemplatesListV2QueryOptions = defaultClient.query({
  url: 'data-ingestion/templates/v2/' as const,
  method: 'GET',
  querySchema: TemplatesListQuerySchema,
  responseSchema: TemplatesListV2ResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data): TemplatesListV2QueryResult {
    // Sort collaborators to put owners first
    for (const template of data.data.items) {
      template.collaborators.sort((collaborator) =>
        collaborator.collaborationLevel === 'owner' ? -1 : 1,
      );
    }
    return data.data;
  },
  keyPrefix: ['templatesList'],
});

export const TemplateCollaboratorSchema = z.object({
  id: z.string(),
  userId: z.number(),
  collaborationLevel: z.enum(['owner', 'collaborator']),
});

export type TemplateCollaborator = z.infer<typeof TemplateCollaboratorSchema>;

const TemplateHeaderSchema = z.object({
  columnPosition: z.number(),
  rawHeader: z.string(),
  mappedHeaderName: z.string().nullable(),
});

const TemplateFormatsSchema = z.object({
  numberFormat: z.string(),
  dateOrder: z.string(),
});

const CreateTemplatePayloadSchema = z.object({
  name: z.string(),
  sheetName: z.string().optional().nullable(),
  ingestionContext: ingestionContextStrictSchema,
  granularityLevel: granularityLevelSchema,
  formats: TemplateFormatsSchema,
  footerRowsCount: z.number(),
  headerRow: z.array(z.string()),
  headers: z.array(TemplateHeaderSchema),
  templateHeaders: z.array(TemplateHeaderSchema),
});

export type CreateTemplatePayload = z.infer<typeof CreateTemplatePayloadSchema>;

export const createTemplate = defaultApi.declareEndpoint({
  url: `data-ingestion/templates/init/` as const,
  method: 'POST',
  requestSchema: CreateTemplatePayloadSchema,
  responseSchema: z.object({}),
});

export const createTemplateV2 = defaultApi.declareEndpoint({
  url: `data-ingestion/templates/init/v2/` as const,
  method: 'POST',
  requestSchema: CreateTemplatePayloadSchema,
  responseSchema: z.object({}),
});

const TemplatePreviewHeaderSchema = z.object({
  columnPosition: z.number(),
  mappedHeaderLabel: z.string().nullable(),
  mappedHeaderName: z.string().nullable(),
  rawHeader: z.string(),
});

const TemplatePreviewSchema = z.object({
  id: z.string(),
  name: z.string(),
  sheetName: z.string().nullable(),
  granularityLevel: granularityLevelSchema,
  ingestionContext: ingestionContextStrictSchema,
  dateOrder: z.string(),
  numberFormat: z.string().optional(),
  footerRowsCount: z.number(),
  headerRow: z.array(z.string()).prefault([]),
  headers: z.array(TemplatePreviewHeaderSchema),
});

const TemplatePreviewResponseSchema = z.object({
  data: z.object({
    items: z.array(TemplatePreviewSchema),
  }),
});

export type TemplatePreviewQueryResult = z.infer<typeof TemplatePreviewSchema>;
export type TemplatePreviewResponse = z.infer<
  typeof TemplatePreviewResponseSchema
>;

export const getTemplatePreviewV1QueryOptions = defaultClient.query({
  url: `data-ingestion/templates/$templateId/preview/` as const,
  method: 'GET',
  responseSchema: TemplatePreviewResponseSchema,
  processResponse(data): TemplatePreviewQueryResult {
    return data.data.items[0];
  },
});

export const getTemplatePreviewV2QueryOptions = defaultClient.query({
  url: `data-ingestion/templates/$templateId/preview/v2/` as const,
  method: 'GET',
  responseSchema: TemplatePreviewResponseSchema,
  processResponse(data): TemplatePreviewQueryResult {
    return data.data.items[0];
  },
});

const TemplateFilterOptionsQuerySchema = z.object({
  include: z.array(z.string()),
});

const TemplateFilterOptionsResponseSchema = z.object({
  data: z.object({
    items: z.array(
      z.object({
        filterType: z.string(),
        options: z.array(
          z.object({
            key: z.string(),
            value: z.string(),
          }),
        ),
      }),
    ),
  }),
});

export type TemplateFilterOptionsResponse = z.infer<
  typeof TemplateFilterOptionsResponseSchema
>;
export type TemplateFilterOptionsQueryResult =
  TemplateFilterOptionsResponse['data']['items'][0];

export const getTemplateFilterOptionsV1QueryOptions = defaultClient.query({
  url: 'data-ingestion/templates/filters/' as const,
  method: 'GET',
  querySchema: TemplateFilterOptionsQuerySchema,
  responseSchema: TemplateFilterOptionsResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data): TemplateFilterOptionsQueryResult {
    return data.data.items[0];
  },
});

export const getTemplateFilterOptionsV2QueryOptions = defaultClient.query({
  url: 'data-ingestion/templates/filters/v2/' as const,
  method: 'GET',
  querySchema: TemplateFilterOptionsQuerySchema,
  responseSchema: TemplateFilterOptionsResponseSchema,
  snakeCaseQueryParams: true,
  processResponse(data): TemplateFilterOptionsQueryResult {
    return data.data.items[0];
  },
});
